import {
  addProps,
  addState,
  addDisplayName,
  addHandlers,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { addApi, addUser } from 'shared/src/api'
import { addEffectOnMount } from 'ad-hok-utils'
import { MediaInput, MediaQueueItem, MediaState } from 'shared/src/api/types'
import { addLocation } from 'shared/src/utils/addNavigate'
import { typedAs } from 'shared/src/utils/typedAs'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { compact } from 'lodash/fp'
import { createUseStyles } from 'react-jss'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { FormProps } from './resource'
import { NumberField, TextField } from './TextField'
import { ResourceSelectField } from './ResourceSelectField'
import { ContentField } from './ImageField'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import { ProductPickerButton } from './post/ProductPickerButton'
import { DetectedProductsModal } from './post/DetectedProductsModal'
import { TagNotificationButton } from './TagNotificationButton'
import { ResourceEditField } from './ResourceEditField'

export interface FormValues
  extends Omit<
    Partial<MediaInput>,
    'productIds' | 'tagIds' | 'state' | 'position'
  > {
  state: MediaState
  position?: string
  productId1?: number
  productId2?: number
  productId3?: number
  productId4?: number
  productId5?: number
  productId6?: number
  tagId1?: number
  tagId2?: number
  tagId3?: number
}

const useStyles = createUseStyles({
  contentAndTagsContainer: {
    display: 'flex',
    gap: 25,
  },
  contentContainer: {
    flex: 1,
  },
  tagsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  tag: {
    display: 'flex',
  },
})

export const MediaForm: FC<FormProps<FormValues>> = flowMax(
  addDisplayName('MediaForm'),
  addUser,
  addLocation,
  addNavigationState(
    'mediaQueueItem',
    'setMediaQueueItem',
    typedAs<Partial<MediaQueueItem> | null>(null)
  ),
  addHandlers({
    clearMediaQueueItem:
      ({ setMediaQueueItem }) =>
      () => {
        setMediaQueueItem(null)
      },
  }),
  addApi,
  addEffectOnMount(({ user, values, mediaQueueItem, onChange }) => () => {
    if (user.isAdmin || values.userId) {
      return
    }

    onChange({
      ...values,
      userId: user.id,
      caption: mediaQueueItem?.caption ?? undefined,
      content: undefined,
    })
  }),
  addHandlers({
    onStateChange:
      ({ values, onChange }) =>
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        onChange({ ...values, state: event.target.value as MediaState })
      },
  }),
  addDataLoadingFromPromise(
    'detectedProducts',
    ({ api, values: { content } }) =>
      content ? api.products.detected(content) : Promise.resolve([]),
    ['api'],
    ({ values: { content } }) =>
      `detectedProducts-${content?.type}-${content?.value}`
  ),
  addCmsLoadingAndErrorIndicators('detectedProducts'),
  addProps(({ detectedProducts }) => ({
    showDetectedProductsButton: detectedProducts.length > 0,
    numDetectedProducts: detectedProducts.flat(1).length,
  })),
  addState(
    'isDetectedProductsModalOpen',
    'setIsDetectedProductsModalOpen',
    false
  ),
  addHandlers({
    onDetectedProductsClick:
      ({ setIsDetectedProductsModalOpen }) =>
      () => {
        setIsDetectedProductsModalOpen(true)
      },
    onDetectedProductChoose:
      ({ setIsDetectedProductsModalOpen, values, onChange }) =>
      (productId: number) => {
        setIsDetectedProductsModalOpen(false)

        const productIds = compact([
          values.productId1,
          values.productId2,
          values.productId3,
          values.productId4,
          values.productId5,
          values.productId6,
        ])

        productIds.push(productId)

        onChange({
          ...values,
          productId1: productIds[0],
          productId2: productIds[1],
          productId3: productIds[2],
          productId4: productIds[3],
          productId5: productIds[4],
          productId6: productIds[5],
        })
      },
  }),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    id,
    user,
    values,
    onStateChange,
    onChange,
    errors,
    t,
    classes,
    mediaQueueItem,
    clearMediaQueueItem,
    showDetectedProductsButton,
    numDetectedProducts,
    onDetectedProductsClick,
    detectedProducts,
    isDetectedProductsModalOpen,
    setIsDetectedProductsModalOpen,
    onDetectedProductChoose,
  }) => (
    <div>
      {user.isAdmin && (
        <ResourceSelectField
          id="userId"
          label={t('admin.resources.media.fields.user')}
          error={errors.userId}
          resourceName="users"
          selectedId={values.userId}
          onChange={(creatorId) => onChange({ ...values, userId: creatorId })}
          focusOnMount
        />
      )}
      <select value={values.state} onChange={onStateChange}>
        <option value="adminDraft">
          {t('admin.resources.media.fields.state.adminDraft')}
        </option>
        <option value="creatorDraft">
          {t('admin.resources.media.fields.state.creatorDraft')}
        </option>
        <option value="published">
          {t('admin.resources.media.fields.state.published')}
        </option>
      </select>
      <NumberField
        id="position"
        label={t('admin.resources.media.fields.position')}
        value={values.position}
        onChange={(position) => onChange({ ...values, position })}
        error={errors.position}
      />
      <TextField
        id="caption"
        label={t('admin.resources.media.fields.caption')}
        value={values.caption}
        onChange={(caption) => onChange({ ...values, caption })}
        error={errors.caption}
        suggestion={mediaQueueItem?.caption}
      />
      <div className={classes.contentAndTagsContainer}>
        <div className={classes.contentContainer}>
          <ContentField
            id="content"
            label={t('admin.resources.media.fields.content')}
            value={values.content}
            onChange={(content) => {
              onChange({ ...values, content })
              clearMediaQueueItem()
            }}
            error={errors.content}
            showShare={!!values.content}
          />
        </div>
        <div className={classes.tagsContainer}>
          <ResourceSelectField
            id="tag1"
            label={t('admin.resources.media.fields.tags')}
            error={errors.tagId1}
            resourceName="tags"
            selectedId={values.tagId1}
            onChange={(tagId1) => onChange({ ...values, tagId1 })}
          />
          {id && values.userId && values.tagId1 && (
            <TagNotificationButton
              tagId={values.tagId1}
              creatorId={values.userId}
              mediaId={id}
            />
          )}
          <ResourceSelectField
            id="tag2"
            error={errors.tagId2}
            resourceName="tags"
            selectedId={values.tagId2}
            onChange={(tagId2) => onChange({ ...values, tagId2 })}
          />
          {id && values.userId && values.tagId2 && (
            <TagNotificationButton
              tagId={values.tagId2}
              creatorId={values.userId}
              mediaId={id}
            />
          )}
          <ResourceSelectField
            id="tag3"
            error={errors.tagId3}
            resourceName="tags"
            selectedId={values.tagId3}
            onChange={(tagId3) => onChange({ ...values, tagId3 })}
          />
          {id && values.userId && values.tagId3 && (
            <TagNotificationButton
              tagId={values.tagId3}
              creatorId={values.userId}
              mediaId={id}
            />
          )}
        </div>
      </div>
      {showDetectedProductsButton && (
        <ProductPickerButton
          type="detected"
          titleStyle="long"
          number={numDetectedProducts}
          onClick={onDetectedProductsClick}
        />
      )}
      <ResourceEditField
        id="product1"
        label={t('admin.resources.media.fields.products')}
        error={errors.productId1}
        resourceName="products"
        selectedId={values.productId1}
        onChange={(productId1) => onChange({ ...values, productId1 })}
      />
      <ResourceEditField
        id="product2"
        label={t('admin.resources.media.fields.products')}
        error={errors.productId2}
        resourceName="products"
        selectedId={values.productId2}
        onChange={(productId2) => onChange({ ...values, productId2 })}
      />
      <ResourceEditField
        id="product3"
        label={t('admin.resources.media.fields.products')}
        error={errors.productId3}
        resourceName="products"
        selectedId={values.productId3}
        onChange={(productId3) => onChange({ ...values, productId3 })}
      />
      <ResourceEditField
        id="product4"
        label={t('admin.resources.media.fields.products')}
        error={errors.productId4}
        resourceName="products"
        selectedId={values.productId4}
        onChange={(productId4) => onChange({ ...values, productId4 })}
      />
      <ResourceEditField
        id="product5"
        label={t('admin.resources.media.fields.products')}
        error={errors.productId5}
        resourceName="products"
        selectedId={values.productId5}
        onChange={(productId5) => onChange({ ...values, productId5 })}
      />
      <ResourceEditField
        id="product6"
        label={t('admin.resources.media.fields.products')}
        error={errors.productId6}
        resourceName="products"
        selectedId={values.productId6}
        onChange={(productId6) => onChange({ ...values, productId6 })}
      />
      {isDetectedProductsModalOpen && (
        <DetectedProductsModal
          groups={detectedProducts}
          onClose={() => {
            setIsDetectedProductsModalOpen(false)
          }}
          onProductChoose={onDetectedProductChoose}
        />
      )}
    </div>
  )
)
